import { useEffect, useRef, type RefObject } from 'react';

/** Ennyi húzás után csukódik le a lap (vagy a lap magasságának negyede, ha az kisebb). */
const CLOSE_DISTANCE_PX = 120;
/** Ennél gyorsabb lefelé pöccintés rövid úton is lecsukja (px/ms). */
const CLOSE_VELOCITY = 0.6;
/** Ennyi mozgás kell, hogy eldőljön: húzás vagy koppintás/görgetés. */
const DRAG_SLOP_PX = 8;
const EXIT_MS = 180;

/**
 * Alulról felcsúszó lap lecsukása LEFELÉ HÚZÁSSAL (Geri, #71: „minden lentre
 * beugró ablakot swipe down-nal is be lehessen csukni").
 *
 * - Érintéssel a lap BÁRMELY pontjáról indítható, ha alatta nincs felfelé
 *   görgetett lista (különben a lista görgetése nyer) és nem szövegmezőben
 *   indul; egérrel csak a fogantyúról (`SheetGrip`, `data-sheet-grip`).
 * - Lefelé húzva a lap követi az ujjat; elengedéskor elég hosszú út vagy
 *   gyors pöccintés esetén kicsúszik és bezárul, különben visszaugrik.
 * - A lap `translate` tulajdonságát mozgatja, NEM a `transform`-ot: a lapok
 *   saját becsúszó animációja a `transform`-on fut, a kettő így nem ütközik.
 */
export function useSwipeToClose<T extends HTMLElement>(
  onClose: () => void,
  enabled = true,
  /** Ha a lap panele cserélődik (pl. „kész" állapot), ennek változása köti újra a figyelést. */
  rebindKey?: unknown,
): RefObject<T> {
  const panelRef = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || !enabled) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    let tracking = false;
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let offset = 0;

    /** Felfelé görgetett lista van-e a kiindulópont és a lap között — akkor a görgetés nyer. */
    const insideScrolledList = (target: EventTarget | null): boolean => {
      let node = target instanceof Element ? target : null;
      while (node && node !== panel.parentElement) {
        if (node instanceof HTMLElement && node.scrollTop > 0 && node.scrollHeight > node.clientHeight) return true;
        node = node.parentElement;
      }
      return false;
    };

    const isTextInput = (target: EventTarget | null): boolean =>
      target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

    const setOffset = (value: number, transition: string) => {
      offset = value;
      panel.style.transition = transition;
      panel.style.translate = value > 0 ? `0 ${value}px` : '';
    };

    const begin = (x: number, y: number) => {
      tracking = true;
      dragging = false;
      startX = x;
      startY = y;
      startTime = performance.now();
    };

    /** `true`, ha a mozdulat a lapé (a hívó ilyenkor megakadályozza a görgetést). */
    const move = (x: number, y: number): boolean => {
      if (!tracking) return false;
      const dy = y - startY;
      if (!dragging) {
        if (Math.abs(dy) < DRAG_SLOP_PX && Math.abs(x - startX) < DRAG_SLOP_PX) return false;
        // Felfelé vagy oldalra induló mozdulat nem a lapé: görgetés, lapozás.
        if (dy <= 0 || Math.abs(x - startX) > Math.abs(dy)) {
          tracking = false;
          return false;
        }
        dragging = true;
      }
      setOffset(Math.max(0, dy), 'none');
      return true;
    };

    const end = () => {
      if (!tracking) return;
      tracking = false;
      if (!dragging) return;
      dragging = false;
      const velocity = offset / Math.max(1, performance.now() - startTime);
      const threshold = Math.min(CLOSE_DISTANCE_PX, panel.offsetHeight / 4);
      if (offset > threshold || velocity > CLOSE_VELOCITY) {
        if (reduceMotion) {
          closeRef.current();
          return;
        }
        setOffset(panel.offsetHeight + 40, `translate ${EXIT_MS}ms ease-in`);
        window.setTimeout(() => closeRef.current(), EXIT_MS - 10);
      } else {
        setOffset(0, reduceMotion ? 'none' : 'translate 200ms ease-out');
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch || event.touches.length > 1) return;
      if (isTextInput(event.target) || insideScrolledList(event.target)) return;
      begin(touch.clientX, touch.clientY);
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch && move(touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
    };

    // Egér (asztal): csak a fogantyúról — a lap többi része kattintható tartalom.
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      if (!(event.target instanceof Element) || !event.target.closest('[data-sheet-grip]')) return;
      event.preventDefault();
      begin(event.clientX, event.clientY);
      const onPointerMove = (moveEvent: PointerEvent) => move(moveEvent.clientX, moveEvent.clientY);
      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        end();
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };

    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
    panel.addEventListener('pointerdown', onPointerDown);
    return () => {
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove', onTouchMove);
      panel.removeEventListener('touchend', end);
      panel.removeEventListener('touchcancel', end);
      panel.removeEventListener('pointerdown', onPointerDown);
    };
  }, [enabled, rebindKey]);

  return panelRef;
}
